import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axiosInstance';
import Select from "react-select";
import { Button } from "react-bootstrap";
import { FaPlusCircle } from "react-icons/fa";
import RecipeCard from '../../components/RecipeCard';
import '../../styles/AdminRecipeListPage.css';
import logger from '../../utils/logger';
import { API_ENDPOINTS } from '../../utils/constants';

const AdminRecipeListPage = () => {
  const [recipes, setRecipes] = useState([]);
  const [selectedRecipeId, setSelectedRecipeId] = useState('');
  const [selectedRecipeDetails, setSelectedRecipeDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [success, setSuccess] = useState('');

  const navigate = useNavigate();

  const fetchRecipes = async () => {
    setLoading(true);
    try {
      const response = await api.get(API_ENDPOINTS.ADMIN_RECIPES);
      if (response.data.success && response.data.list) {
        const formattedRecipes = response.data.list.map((recipe) => ({
          id: recipe.recipeId,
          name: recipe.name,
        }));
        setRecipes(formattedRecipes);
        setError('');
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      logger.error('Error fetching recipes:', err);
      setError('Failed to fetch recipes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecipeById = async (recipeId) => {
    if (!recipeId) return;

    setLoading(true);
    try {
      const response = await api.get(API_ENDPOINTS.ADMIN_RECIPE(recipeId));
      if (response.data.recipe) {
        setSelectedRecipeDetails(response.data.recipe);
      } else {
        throw new Error('Recipe not found');
      }
      setError('');
    } catch (err) {
      logger.error('Error fetching recipe by ID:', err);
      setError('Failed to fetch recipe details.');
      setSelectedRecipeDetails(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDropdownChange = (selectedOption) => {
    if (selectedOption) {
      setSelectedRecipeId(selectedOption.value);
      fetchRecipeById(selectedOption.value);
    } else {
      setSelectedRecipeId('');
      setSelectedRecipeDetails(null);
    }
  };

  const confirmDelete = () => {
    setShowDeleteModal(true);
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
  };

  const executeDelete = async () => {
    if (!selectedRecipeId) return;

    setShowDeleteModal(false);
    setDeleting(true);

    try {
      await api.post(API_ENDPOINTS.ADMIN_RECIPE_DELETE(selectedRecipeId));
      setSuccess('Recipe deleted successfully!');
      await fetchRecipes();
      setSelectedRecipeDetails(null);
      setSelectedRecipeId('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      logger.error('Error deleting recipe:', err);
      setError(err.response?.data?.error || 'Failed to delete recipe.');
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    fetchRecipes();
  }, []);

  return (
    <div className="admin-recipes-container">
      {success && (
        <div className="success-message-container">
          <div className="success-message28">
            <span>{success}</span>
          </div>
        </div>
      )}

      <div className="recipe-left-section">
        <h2>Recipes</h2>
        <div className="recipe-controls-container">
          <Select
            options={recipes.map((recipe) => ({
              value: recipe.id,
              label: recipe.name,
            }))}
            value={selectedRecipeId ? {
              value: selectedRecipeId,
              label: recipes.find((r) => r.id === selectedRecipeId)?.name || '',
            } : null}
            placeholder="Select a Recipe"
            onChange={handleDropdownChange}
            className="recipe-custom-dropdown"
            isSearchable
            isClearable
          />
          <Button
            className="recipe-btn-create"
            onClick={() => navigate('/admin/recipes/create')}
          >
            <FaPlusCircle /> New Recipe
          </Button>
        </div>
        {error && <p className="recipe-error-text">{error}</p>}
      </div>

      <div className="recipe-right-section">
        {selectedRecipeDetails ? (
          <div className="recipe-card-wrapper">
            <RecipeCard recipe={selectedRecipeDetails} />
            <div className="recipe-action-buttons">
              <Button
                className="recipe-btn-edit"
                onClick={() => navigate(`/admin/recipes/${selectedRecipeId}`)}
              >
                Edit
              </Button>
              <Button
                className="recipe-btn-delete"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="recipe-centered-message">
            <p>Select a recipe to view details.</p>
          </div>
        )}
      </div>

      {showDeleteModal && (
        <div className="recipe-modal-overlay">
          <div className="recipe-delete-modal">
            <h3>Confirm Deletion</h3>
            <p>Are you sure you want to delete this recipe?</p>
            <p>Recipe: {selectedRecipeDetails?.title}</p>
            <div className="recipe-modal-buttons">
              <button
                className="recipe-modal-button recipe-modal-cancel"
                onClick={cancelDelete}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="recipe-modal-button recipe-modal-confirm"
                onClick={executeDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRecipeListPage;
